import React from "react";

import { FartherManagedAccount } from "@fartherfinance/frontend/api/Accounts/Types";
import useGetAcatsInstitutions from "@fartherfinance/frontend/api/Transfer/hooks/useGetAcatsInstitutions";

import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import FormCheckboxField from "@src/sharedComponents/Forms/FormCheckboxField";
import FormDropdownField from "@src/sharedComponents/Forms/FormDropdownField";
import FormNumberField from "@src/sharedComponents/Forms/FormNumberField";
import FormTextField from "@src/sharedComponents/Forms/FormTextField";
import FormFieldLabel from "@src/sharedComponents/Forms/Private/FormFieldLabel";
import SingleFileDropUpload from "@src/sharedComponents/SingleFileDropUpload/SingleFileDropUpload";

import styles from "../CreateTransferSummary.module.css";

interface CreateTransferInvestmentFormProps {
  managedAccounts: FartherManagedAccount[];
  statementFile: File | null;
  setStatementFile: (file: File | null) => void;
}

const accountTypes = ["INDIVIDUAL", "JOINT", "TRUST"] as const;

const CreateTransferInvestmentForm: React.FC<
  CreateTransferInvestmentFormProps
> = ({ managedAccounts, setStatementFile, statementFile }) => {
  const auth = useRequestAuth();

  const acatInstitutions = useGetAcatsInstitutions(auth);

  const investmentTypeOptions = accountTypes.map((acctType: string) => {
    const label: string =
      acctType === "INDIVIDUAL"
        ? "Individual"
        : acctType === "JOINT"
        ? "Joint"
        : "Trust";

    return {
      accountType: acctType,
      label,
    };
  });

  const institutionList = acatInstitutions.data?.institutions || [];

  const fartherAccountList = Array.isArray(managedAccounts)
    ? managedAccounts.map((acct) => ({
        accountType: acct.accountDetails.accountType,
        label: acct.accountDetails.accountType,
        balance: acct.accountDetails.lastReportedBalance.balance,
        accountNumber: acct.accountDetails.custodianAccountNumber,
        accountId: acct.virtualAccountId,
      }))
    : [];

  return (
    <>
      <FormDropdownField
        name="accountType"
        label="Account Type"
        disableSearch
        values={investmentTypeOptions}
        required="Please select an account type"
      />

      <FormDropdownField
        name="institution"
        label="Institution"
        disableSearch
        values={institutionList.map((ins) => ({
          label: ins.displayName,
          institutionId: ins.institutionId,
        }))}
        required="Please select an institution"
      />

      <FormNumberField
        name="accountNumber"
        label="Account Number"
        required="Please provide an account number"
      />

      <FormTextField
        name={"accountHolderName"}
        label={"Account Name Holder"}
        required={"Account Holder Name is required"}
      />

      <FormDropdownField
        name={"accountDestination"}
        label={"Account Destination"}
        disableSearch
        values={fartherAccountList.map((destination) => ({
          ...destination,
          label: `${destination.label} ($${
            destination.balance ? destination.balance.toFixed(2) : "0.00"
          })`,
        }))}
        required="Please select an account destination"
      />
      <div className={styles.statementContainer}>
        <FormFieldLabel
          label={<div className={styles.label}>Upload a statement</div>}
        />
        <SingleFileDropUpload
          file={statementFile}
          onChange={setStatementFile}
          className={styles.fileFrame}
        />
        <FormCheckboxField
          label="Click here to sign this transfer request."
          name="requestSigned"
        />
      </div>
    </>
  );
};

export default CreateTransferInvestmentForm;
